import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Dialog, Grid } from '@shared/components';
import { EMAIL_REGEX } from '@shared/constants';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import useStyle from './style';

const noop = () => {};

const defaultState = {
  isLoading: false,
  entries: [],
  totalEntries: 0,
  pageSize: 5,
  pageNumber: 1,
  sortBy: null,
  sortOrder: null,
};

const ErrorDialog = ({
  open = false,
  heading = '',
  data = [],
  onSync = noop,
  onClose = noop,
}) => {
  const classes = useStyle();
  const [state, setState] = useState(defaultState);

  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      entries: validateDuplicacy(data || []).map(item => ({
        ...item,
        extensionNumber: item.extensionNumber ? +item.extensionNumber : '',
        contact: {
          ...item.contact,
          firstName: item?.contact?.firstName || '-',
          lastName: item?.contact?.lastName || '-',
        },
      })),
      totalEntries: (data || []).length,
    }));
  }, [data]);

  const descendingComparator = (a, b, orderBy) => {
    const splittedOrderBy = (orderBy || '').split('.');
    [...Array(splittedOrderBy.length)].map((item, index) => {
      a = a && a[splittedOrderBy[index]];
      b = b && b[splittedOrderBy[index]];
    });

    if (b < a) {
      return -1;
    }
    if (b > a) {
      return 1;
    }
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'DESC'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
  };

  const validateDuplicacy = (extensions = []) => {
    const duplicateEntriesEmail = extensions
      .filter(
        (extension, index) =>
          index !==
          extensions.findIndex(
            item =>
              (item?.contact?.email || '').toLowerCase() ===
              (extension?.contact?.email || '').toLowerCase()
          )
      )
      .map(extension => (extension?.contact?.email || '').toLowerCase());

    const duplicateEntries = extensions
      .filter(extension =>
        duplicateEntriesEmail.includes(
          (extension?.contact?.email || '').toLowerCase()
        )
      )
      .map(item => item.id);

    return extensions.map(extension => ({
      ...extension,
      hasError: duplicateEntries.includes(extension?.id),
    }));
  };

  const handleEmailChange = (evt, id) => {
    const { value } = evt.currentTarget;
    setState(prevState => {
      const hasError =
        !EMAIL_REGEX.test(value) ||
        prevState.entries.some(entry => entry?.contact?.email === value);
      let updatedEntries = prevState.entries.map(entry => {
        if (entry.id === id) {
          return {
            ...entry,
            contact: {
              ...entry.contact,
              email: value,
            },
            hasError,
          };
        }
        return entry;
      });
      return {
        ...prevState,
        entries: validateDuplicacy(updatedEntries),
      };
    });
  };

  const columnConfig = [
    {
      field: 'contact.email',
      label: 'EMAIL',
      canSort: true,
      render: row => (
        <TextField
          error={row.hasError}
          variant='outlined'
          size='small'
          fullWidth
          label='Email'
          defaultValue={row.contact?.email}
          //value={row.contact?.email}
          onChange={evt => handleEmailChange(evt, row.id)}
          classes={{
            root: classes.emailInput,
          }}
        />
      ),
    },
    {
      field: 'extensionNumber',
      label: 'EXTENSION',
      canSort: true,
      render: row => (
        <Tooltip title={row.extensionNumber || ''} placement='top-start'>
          <Typography variant='body2' noWrap>
            {row.extensionNumber || '-'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'contact.firstName',
      label: 'FIRST NAME',
      canSort: true,
      render: row => (
        <Tooltip title={row.contact?.firstName || ''} placement='top-start'>
          <Typography variant='body2' noWrap>
            {row.contact?.firstName || '-'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'contact.lastName',
      label: 'LAST NAME',
      canSort: true,
      render: row => (
        <Tooltip title={row.contact?.lastName || ''} placement='top-start'>
          <Typography variant='body2' noWrap>
            {row.contact?.lastName || '-'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'type',
      label: 'EXTENSION TYPE',
      canSort: true,
      render: row => (
        <Tooltip title={row.type || ''} placement='top-start'>
          <Typography variant='body2' noWrap>
            {row.type || '-'}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  const handlePageSizeChange = useCallback(
    (pageSize = defaultState.pageSize) => {
      setState(prevState => ({
        ...prevState,
        pageSize,
        pageNumber: defaultState.pageNumber,
      }));
    },
    []
  );

  const handlePageNumberChange = useCallback(
    (pageNumber = defaultState.pageNumber) => {
      setState(prevState => ({
        ...prevState,
        pageNumber,
      }));
    },
    []
  );

  const handleSortChange = useCallback((sortBy = null, sortOrder = null) => {
    setState(prevState => ({
      ...prevState,
      sortBy,
      sortOrder: sortOrder || 'ASC',
    }));
  }, []);

  const gridEntries = stableSort(
    state.entries,
    getComparator(state.sortOrder, state.sortBy)
  ).slice(
    (state.pageNumber - 1) * state.pageSize,
    (state.pageNumber - 1) * state.pageSize + state.pageSize
  );

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      open={open}
      fullWidth
      maxWidth='xl'
      onClose={onClose}
      classes={{
        paper: classes.errorDialog,
      }}
      BackdropProps={{
        classes: {
          root: classes.backDrop,
        },
      }}
    >
      <Dialog.Title>
        <div className='d-flex f-align-center f-justify-between'>
          <Typography variant='h6'>{heading}</Typography>
          <div className='d-flex f-align-center'>
            <Button variant='outlined' onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              disabled={state.entries.some(entry => entry.hasError)}
              className='ml-5'
              onClick={() => onSync(state.entries)}
            >
              Sync Changes
            </Button>
          </div>
        </div>
      </Dialog.Title>
      <Dialog.Content>
        <Grid
          hasLoader
          showRowDivider
          columns={columnConfig}
          data={gridEntries}
          total={state.totalEntries}
          isLoading={state.isLoading}
          pageSize={state.pageSize}
          pageNumber={state.pageNumber}
          hasSelection={false}
          sortBy={columnConfig.findIndex(
            column => column.field === state.sortBy
          )}
          sortOrder={state.sortOrder}
          onSort={handleSortChange}
          onPageSizeChange={handlePageSizeChange}
          onPageNumberChange={handlePageNumberChange}
          classes={{
            container: classes.errorWrapper,
          }}
        />
      </Dialog.Content>
    </Dialog>
  );
};

export default ErrorDialog;
